import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ONEGO_LOGO } from "../lib/api";
import { toast } from "sonner";
import { Locate } from "lucide-react";

const COUNTRIES = ["Kenya", "South Africa", "Nigeria", "Ghana", "Uganda", "Tanzania"];

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        name: "",
        business_name: "",
        email: "",
        phone: "",
        country: "Kenya",
        password: "",
        region: "urban",
        address_label: "",
        lat: "",
        lng: "",
    });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const detect = () => {
        if (!navigator.geolocation) {
            toast.error("Geolocation not supported");
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setForm((f) => ({ ...f, lat: pos.coords.latitude.toFixed(5), lng: pos.coords.longitude.toFixed(5) }));
                toast.success("Location captured");
            },
            (err) => toast.error(err.message),
            { enableHighAccuracy: true, timeout: 10000 },
        );
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const payload = {
            ...form,
            lat: form.lat ? Number(form.lat) : null,
            lng: form.lng ? Number(form.lng) : null,
        };
        const res = await register(payload);
        setLoading(false);
        if (res.ok) {
            toast.success("Welcome to OneGo!");
            navigate("/dashboard");
        } else {
            setError(res.error);
            toast.error(res.error);
        }
    };

    return (
        <div className="min-h-screen og-gradient-soft flex items-center justify-center px-6 py-12" data-testid="register-page">
            <div className="w-full max-w-lg og-card p-8">
                <Link to="/" className="inline-flex items-center" data-testid="register-logo-link">
                    <img src={ONEGO_LOGO} alt="OneGo" className="h-10 w-auto" />
                </Link>
                <h1 className="mt-6 font-display font-extrabold text-3xl text-onego-navy">Join the network</h1>
                <p className="mt-2 text-slate-600">Open a free merchant account in under a minute.</p>

                <form onSubmit={onSubmit} className="mt-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-semibold text-onego-navy">Full name</label>
                            <input className="og-input mt-1" required value={form.name} onChange={upd("name")} placeholder="Amina Otieno" data-testid="register-name-input" />
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-onego-navy">Business name</label>
                            <input className="og-input mt-1" value={form.business_name} onChange={upd("business_name")} placeholder="Amina's Kiosk" data-testid="register-business-input" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-semibold text-onego-navy">Email</label>
                            <input type="email" className="og-input mt-1" required value={form.email} onChange={upd("email")} placeholder="you@business.africa" data-testid="register-email-input" />
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-onego-navy">Phone</label>
                            <input className="og-input mt-1" value={form.phone} onChange={upd("phone")} placeholder="+254\u2026" data-testid="register-phone-input" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-semibold text-onego-navy">Country</label>
                            <select className="og-input mt-1" value={form.country} onChange={upd("country")} data-testid="register-country-select">
                                {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-onego-navy">Region</label>
                            <select className="og-input mt-1" value={form.region} onChange={upd("region")} data-testid="register-region-select">
                                <option value="urban">Urban</option>
                                <option value="rural">Rural</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-onego-navy">Address / kiosk location</label>
                        <input className="og-input mt-1" value={form.address_label} onChange={upd("address_label")} placeholder="Stall 14, Westlands market" data-testid="register-address-input" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="text-sm font-semibold text-onego-navy">Latitude</label>
                            <input className="og-input mt-1" value={form.lat} onChange={upd("lat")} placeholder="-1.2921" data-testid="register-lat-input" />
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-onego-navy">Longitude</label>
                            <input className="og-input mt-1" value={form.lng} onChange={upd("lng")} placeholder="36.8219" data-testid="register-lng-input" />
                        </div>
                        <div className="flex items-end">
                            <button type="button" onClick={detect} className="og-btn-ghost w-full inline-flex items-center justify-center gap-1 text-sm" data-testid="register-detect-btn">
                                <Locate size={14} /> Detect
                            </button>
                        </div>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-onego-navy">Password</label>
                        <input type="password" className="og-input mt-1" required minLength={6} value={form.password} onChange={upd("password")} placeholder="6+ characters" data-testid="register-password-input" />
                    </div>
                    {error && <p className="text-sm text-rose-600 font-semibold" data-testid="register-error">{error}</p>}
                    <button type="submit" disabled={loading} className="og-btn-gradient w-full disabled:opacity-60" data-testid="register-submit-btn">
                        {loading ? "Creating account\u2026" : "Create account"}
                    </button>
                </form>
                <p className="mt-6 text-sm text-slate-600">
                    Already on OneGo?{" "}
                    <Link to="/login" className="font-bold text-onego-navy hover:text-pink-600" data-testid="register-login-link">Sign in</Link>
                </p>
            </div>
        </div>
    );
}
